import React from "react";
import {Button} from "../ui/button";
import Footer from "./ui/Footer";
import {Input} from "../ui/input";
import {Select, SelectContent, SelectItem, SelectTrigger} from "../ui/select";
import {Textarea} from "../ui/textarea";
import {withTranslation} from "react-i18next";
import {Question} from "src/interfaces";
import {WidgetContent} from "./Widget";
import Joi from "joi";
import {fromJson} from "json-joi-converter";
import {Checkbox} from "../ui/checkbox";
import {Switch} from "../ui/switch";
import {RadioGroup, RadioGroupItem} from "../ui/radio";
import {connect} from "react-redux";
import {scanKnowledgeBase} from "src/utilities/scanKB";
import {KBSettings} from "src/interfaces/settings";
import {Form as IForm} from "src/interfaces/form";
import {parsePhoneNumberFromString} from "libphonenumber-js";
import Header from "./Header";
import {t} from "i18next";
import fuzzysort from "fuzzysort";
import {KEYS_OF_INTEREST, MIN_SCORE_FOR_SUGGESTED_QUESTIONS} from "src/config/constants";

interface FormProps {
  language: string;
  questions: Question[];
  settings: KBSettings;
  selectedForm: IForm;
  formContent: any;
  formState: any;
  showSearch: boolean;
  closeButton: React.ReactNode;
  hideHeader?: boolean;
  formsOnly?: boolean;
  setFormState: (formState: any, callback?: () => void) => void;
  showSuggestedQuestions: (questions: Question[]) => void;
  setValidatedForm: (form: Joi.ValidationResult) => void;
  navigateTo: (newState) => void;
  handleBackButtonClick: (fromWidgetContent: WidgetContent) => void;
  handleSearchButtonClick: () => void;
}

interface FormState {
  validationSchema: Joi.Schema;
  fieldErrors: any;
  formTouchedState: any;
}

interface QuestionScore {
  question: Question;
  score: number;
}

class Form extends React.Component<FormProps, FormState> {
  constructor(props) {
    super(props);
    this.state = {
      validationSchema: fromJson(props.formContent.validation),
      fieldErrors: {},
      formTouchedState: Object.keys(this.props.formState).reduce((acc, key) => {
        acc[key] = false;
        return acc;
      }, {}),
    };
  }

  render() {
    return (
      <React.Fragment>
        {!this.props.hideHeader && (
          <Header
            widgetSettings={this.props.settings?.widgetSettings}
            languageCode={this.props.language}
            showSearch={this.props.showSearch === undefined ? true : this.props.showSearch}
            closeButton={this.props.closeButton}
            onBackButtonClick={() => this.props.handleBackButtonClick(WidgetContent.FORM)}
            onSearchButtonClick={() => this.props.handleSearchButtonClick()}
          />
        )}
        <main className="faq-widget-main" style={{paddingTop: this.props.formsOnly ? "24px" : ""}}>
          <div className="faq-widget-main-top">
            <h2>{this.props.selectedForm.name || "Form"}</h2>
            <form className="flex flex-col gap-4 mb-2">{this.renderFormElements()}</form>
            <Button
              id="submit-form"
              aria-label={t("SUBMIT")}
              title={t("SUBMIT")}
              onClick={(e) => this.isFormValid()}
              className="w-fit rounded-full"
              style={{
                backgroundColor: this.props.settings?.widgetSettings?.categoryButtonColor,
                color: this.props.settings?.widgetSettings?.categoryButtonTextColor,
              }}
            >
              {t("SUBMIT")}
            </Button>
          </div>
          {this.props.settings?.widgetSettings?.footerEnabled && <Footer />}
        </main>
      </React.Fragment>
    );
  }

  renderFormElements() {
    const layoutElements = this.props.formContent.layout.elements;
    const validationRules = this.props.formContent.validation?.properties;

    return layoutElements.map((element) => {
      const validation = validationRules[element.ref];

      switch (element.ui_type) {
        case "input":
        case "number":
          return this.renderInput(element, validation);
        case "text":
        case "long_text":
        case "html":
          return this.renderTextArea(element, validation);
        case "select":
          return this.renderSelect(element, validation);
        case "list":
          return this.renderList(element, validation);
        case "switch":
          return this.renderSwitch(element, validation);
        case "radio":
          return this.renderRadio(element, validation);
        case "checkbox":
          return this.renderCheckbox(element, validation);
        case "checkboxes":
          return this.renderCheckboxes(element, validation);
        case "file":
          return this.renderFile(element);
        default:
          return null;
      }
    });
  }

  renderInput(element, validation) {
    const label = this.getLabelForElement(element, validation);
    const requiredMark = validation.required ? " *" : "";
    const isPhone = element.ui_type === "input" && !!validation.meta?.phone;
    const isEmail = element.ui_type === "input" && !!validation.email;
    let type = "text";
    let placeholder = "";

    if (isPhone) {
      type = "tel";
      placeholder = "+46123123123";
    } else if (isEmail) {
      type = "email";
      placeholder = "example@email.com";
    } else if (element.ui_type === "number") {
      type = "number";
    }

    return (
      <div key={element.ref}>
        <label htmlFor={element.ref}>{`${label}${requiredMark}`}</label>
        <Input
          id={element.ref}
          type={type}
          aria-label={label}
          placeholder={placeholder}
          value={this.props.formState[element.ref] || ""}
          onChange={(e) => this.handleChange(element.ref, e.target.value)}
          onBlur={() => this.validateField(element.ref, this.props.formState[element.ref], isPhone)}
          className={`${
            this.state.fieldErrors[element.ref] ? "border-destructive border-2" : ""
          } bg-background rounded-md`}
        />
      </div>
    );
  }

  renderSelect(element, validation) {
    const label = this.getLabelForElement(element, validation);
    const requiredMark = validation.required ? " *" : "";
    const translatedSelectedOption = this.getOptionTitle(
      element.options.find((option) => option.key === this.props.formState[element.ref]),
    );

    return (
      <div key={element.ref}>
        <label>{`${label}${requiredMark}`}</label>
        <Select
          defaultValue={null}
          onValueChange={(value) => {
            this.handleChange(element.ref, value);
          }}
        >
          <SelectTrigger
            id={element.ref}
            aria-label={label}
            className={`${
              this.state.fieldErrors[element.ref] ? "border-destructive border-2" : ""
            } bg-background rounded-md`}
          >
            {translatedSelectedOption || t("SELECT_AN_OPTION")}
          </SelectTrigger>
          <SelectContent>
            {element.options.map((option) => {
              const optionTitle = this.getOptionTitle(option);
              return (
                <SelectItem key={option.key} value={option.key} aria-label={optionTitle} title={optionTitle}>
                  {optionTitle}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </div>
    );
  }

  renderList(element, validation) {
    const label = this.getLabelForElement(element, validation);
    const requiredMark = validation.required || validation.when?.then?.required ? " *" : "";
    const values = this.props.formState[element.ref] || [];
    const itemIsPhone = !!validation.items?.meta?.phone;
    const itemIsEmail = !!validation.items?.email;
    const itemType = itemIsPhone ? "tel" : itemIsEmail ? "email" : "text";

    return (
      <div key={element.ref} className="relative">
        <label htmlFor={element.ref}>{`${label}${requiredMark}`}</label>
        {values.map((value, index) => (
          <Input
            id={`${element.ref}_${index}`}
            aria-label={label}
            title={label}
            key={index}
            type={itemType}
            placeholder={itemIsPhone ? "+46123123123" : itemIsEmail ? "example@email.com" : ""}
            value={value}
            onChange={(e) => {
              this.handleChangeEntry(element.ref, index, e.target.value);
              if (this.state.fieldErrors[`${element.ref}_${index}`]) {
                this.validateField(element.ref, [e.target.value], itemIsPhone, index);
              }
            }}
            onBlur={() => {
              this.validateField(element.ref, [value], itemIsPhone, index);
            }}
            className={`${
              this.state.fieldErrors[`${element.ref}_${index}`] ? "border-destructive border-2" : ""
            } mb-1 bg-background rounded-md`}
          />
        ))}
        <div className="absolute top-0 right-0">
          {values.length > 1 && (
            <>
              <label htmlFor={`${element.ref}_remove`} className="sr-only">
                {t("REMOVE_FIELD") + " " + label}
              </label>
              <Button
                id={`${element.ref}_remove`}
                aria-label={t("REMOVE_FIELD") + " " + label}
                title={t("REMOVE_FIELD") + " " + label}
                onClick={() => this.handleRemoveEntry(element.ref)}
                variant="ghost"
                className="h-2 w-2 p-2 bg-background rounded-md"
              >
                -
              </Button>
            </>
          )}
          <label htmlFor={`${element.ref}_add`} className="sr-only">
            {t("ADD_FIELD") + " " + label}
          </label>
          <Button
            id={`${element.ref}_add`}
            aria-label={t("ADD_FIELD") + " " + label}
            title={t("ADD_FIELD") + " " + label}
            onClick={() => this.handleAddEntry(element.ref)}
            variant="ghost"
            className="h-2 w-2 p-2 ml-1 bg-background rounded-md"
          >
            +
          </Button>
        </div>
      </div>
    );
  }

  renderSwitch(element, validation) {
    const label = this.getLabelForElement(element, validation);
    const requiredMark = validation.required ? " *" : "";

    return (
      <div key={element.ref} className="flex items-center gap-2">
        <label htmlFor={element.ref}>{`${label}${requiredMark}`}</label>
        <Switch
          id={element.ref}
          aria-label={label}
          title={label}
          checked={!!this.props.formState[element.ref]}
          onCheckedChange={(checked) => {
            this.handleChange(element.ref, checked);
          }}
          className={`${this.state.fieldErrors[element.ref] ? "border-destructive border-2" : ""}`}
        />
      </div>
    );
  }

  renderRadio = (element, validation) => {
    const label = this.getLabelForElement(element, validation);
    const requiredMark = validation.required ? " *" : "";

    return (
      <div key={element.ref} className="">
        <label htmlFor={element.ref}>{`${label}${requiredMark}`}</label>
        <RadioGroup
          id={element.ref}
          aria-label={label}
          title={label}
          className="flex flex-col gap-2"
          defaultValue={this.props.formState[element.ref]}
          onValueChange={(value) => this.handleChange(element.ref, value)}
        >
          {element.options.map((option) => {
            const optionTitle = this.getOptionTitle(option);
            return (
              <div key={option.key} className="flex items-center space-x-2">
                <RadioGroupItem
                  aria-label={optionTitle}
                  title={optionTitle}
                  value={option.key}
                  id={option.key}
                  checked={this.props.formState[element.ref] === option.key}
                  className={`${
                    this.state.fieldErrors[element.ref] ? "border-destructive border-2" : ""
                  } bg-background rounded-md`}
                />
                <label htmlFor={option.key}>{optionTitle}</label>
              </div>
            );
          })}
        </RadioGroup>
      </div>
    );
  };

  renderCheckbox = (element, validation) => {
    const label = this.getLabelForElement(element, validation);

    return (
      <div key={element.ref} className="flex items-center gap-2">
        <Checkbox
          id={element.ref}
          aria-label={label}
          title={label}
          checked={!!this.props.formState[element.ref]}
          onCheckedChange={(checked) => {
            this.props.setFormState({...this.props.formState, [element.ref]: checked});
            this.validateField(element.ref, checked);
          }}
          className={`${this.state.fieldErrors[element.ref] ? "border-destructive border-2" : ""}`}
        />
        <label htmlFor={element.ref}>{label}</label>
      </div>
    );
  };

  renderCheckboxes = (element, validation) => {
    const label = this.getLabelForElement(element, validation);
    const requiredMark = validation.required ? " *" : "";

    return (
      <div key={element.ref}>
        <label htmlFor={element.ref}>{`${label}${requiredMark}`}</label>
        {element.options.map((option) => {
          const optionTitle = this.getOptionTitle(option);
          return (
            <label htmlFor={option.key} key={option.key} className="flex items-center space-x-2 gap-2">
              <Checkbox
                id={option.key}
                aria-label={optionTitle}
                title={optionTitle}
                checked={this.props.formState[element.ref]?.includes(option.key)}
                onCheckedChange={(checked) => {
                  this.handleCheckboxGroupChange(element.ref, option.key, checked);
                }}
                className={`${this.state.fieldErrors[element.ref] ? "border-destructive border-2" : ""}`}
              />
              {optionTitle}
            </label>
          );
        })}
      </div>
    );
  };

  renderTextArea(element, validation) {
    const label = this.getLabelForElement(element, validation);
    const requiredMark = element.required ? " *" : "";

    return (
      <div key={element.ref}>
        <label htmlFor={element.ref}>{`${label}${requiredMark}`}</label>
        <Textarea
          id={element.ref}
          aria-label={label}
          title={label}
          value={this.props.formState[element.ref] || ""}
          onChange={(e) => this.handleChange(element.ref, e.target.value)}
          className={`${
            this.state.fieldErrors[element.ref] ? "border-destructive border-2" : ""
          } bg-background rounded-md`}
        />
      </div>
    );
  }

  renderFile(element) {
    const label = this.getLabelForElement(element);
    const requiredMark = element.required ? " *" : "";

    return (
      <div key={element.ref}>
        <label htmlFor={element.ref}>{`${label}${requiredMark}`}</label>
        <Input
          id={element.ref}
          aria-label={label}
          title={label}
          type="file"
          onChange={(e) => this.handleFileChange(element, e.target.files[0])}
          className={`${
            this.state.fieldErrors[element.ref] ? "border-destructive border-2" : ""
          } bg-background rounded-md`}
        />
        {this.state.fieldErrors[element.ref] && (
          <div className="text-destructive text-sm">{this.state.fieldErrors[element.ref]}</div>
        )}
      </div>
    );
  }

  getLabelForElement(element, validation?) {
    const userLang = navigator.language;
    const fallbackLang = userLang?.split("-")?.[0];

    const translations = element.t || {};

    if (element.ui_type === "file") {
      return translations[userLang] || translations[fallbackLang] || element.label;
    }

    const label = translations[userLang] || translations[fallbackLang] || validation?.label;

    return label;
  }

  getOptionTitle(option) {
    if (!option) {
      return "";
    }
    const userLang = navigator.language;
    const defaultTitle = option.title;
    const optionTitle = option.t?.[userLang] || option.t?.[userLang.split("-")[0]];

    return optionTitle || defaultTitle;
  }

  handleChange(elementRef, value) {
    const validation = this.props.formContent.validation.properties[elementRef];
    const isPhone = validation?.meta?.phone;

    this.props.setFormState({...this.props.formState, [elementRef]: value});

    if (this.state.fieldErrors[elementRef]) {
      this.validateField(elementRef, value, isPhone);
    }
  }

  handleCheckboxGroupChange(field, optionKey, isChecked) {
    const updatedOptions = isChecked
      ? [...(this.props.formState[field] || []), optionKey]
      : (this.props.formState[field] || []).filter((key) => key !== optionKey);

    this.props.setFormState(
      {
        ...this.props.formState,
        [field]: updatedOptions,
      },
      () => this.validateField(field, updatedOptions),
    );
  }

  handleAddEntry(field: string) {
    const currentValues = this.props.formState[field] || [];

    const newValues = currentValues.length === 0 ? [""] : [...currentValues, ""];
    this.props.setFormState({
      ...this.props.formState,
      [field]: newValues,
    });
  }

  handleRemoveEntry(field: string) {
    const currentValues = this.props.formState[field];
    if (Array.isArray(currentValues) && currentValues.length > 0) {
      const updatedValues = currentValues.slice(0, -1);
      this.props.setFormState({
        ...this.props.formState,
        [field]: updatedValues,
      });
    }
  }

  handleChangeEntry(field: string, index: number, value: string) {
    const currentValues = this.props.formState[field];
    if (Array.isArray(currentValues)) {
      const updatedValues = [...currentValues];
      updatedValues[index] = value;
      this.props.setFormState({
        ...this.props.formState,
        [field]: updatedValues,
      });
    }
  }

  scanKB() {
    const searchString = this.buildSearchString(KEYS_OF_INTEREST);
    const searchTerms = this.getSearchTerms(searchString);
    const questionScores = this.calculateQuestionScores(searchTerms);
    const topQuestions = this.getTopQuestions(questionScores);

    this.showSuggestedQuestions(topQuestions);
  }

  buildSearchString(keysOfInterest: string[]) {
    return this.props.formContent.layout.elements
      .filter((element) => keysOfInterest.includes(element.ui_type))
      .map((element) => this.getElementValue(element))
      .join(" ");
  }

  getElementValue(element) {
    const {ui_type, ref, options} = element;
    const formState = this.props.formState[ref];

    if (["select", "checkboxes", "radio"].includes(ui_type)) {
      const selectedKeys = Array.isArray(formState) ? formState : [formState];
      return options
        .filter((option) => selectedKeys.includes(option.key))
        .map((option) => option.title)
        .join(", ");
    }

    return Array.isArray(formState) ? formState.join(", ") : formState;
  }

  getSearchTerms(searchString: string) {
    return searchString.split(/\s+/).filter((term) => term.length > 0);
  }

  calculateQuestionScores(searchTerms: string[]) {
    let questionScores = {};

    searchTerms.forEach((term) => {
      const {questions} = scanKnowledgeBase(term, this.props.questions, null, this.props.language);
      questions.forEach((question) => {
        this.updateQuestionScore(questionScores, question, term);
      });
    });

    return questionScores;
  }

  updateQuestionScore(questionScores, question, term) {
    const questionLang = question.langs[this.props.language];
    if (!questionLang) return;

    const fuzzyResult = fuzzysort.go(term, [questionLang.question, questionLang.answer]);
    if (fuzzyResult.length > 0) {
      const score = fuzzyResult[0].score;
      if (!questionScores[question.id] || score > questionScores[question.id].score) {
        questionScores[question.id] = {question, score};
      }
    }
  }

  getTopQuestions(questionScores: Record<string, QuestionScore>) {
    return Object.values(questionScores)
      .filter((item) => item.score > MIN_SCORE_FOR_SUGGESTED_QUESTIONS)
      .sort((a, b) => b.score - a.score)
      .map((item) => item.question)
      .filter((q) => !q.deleted)
      .slice(0, 5);
  }

  showSuggestedQuestions(topQuestions: Question[]) {
    this.props?.showSuggestedQuestions && this.props?.showSuggestedQuestions(topQuestions);
  }

  removeEmptyStrings = (formState: any) => {
    const formStateCopy = {...formState};
    Object.keys(formStateCopy).forEach((key) => {
      const fieldValidation = this.props.formContent.validation.properties[key];
      if (Array.isArray(formStateCopy[key]) && fieldValidation?.optional) {
        formStateCopy[key] = formStateCopy[key].filter(
          (item) => item !== "" || (fieldValidation.allow && fieldValidation.allow.includes("")),
        );
      } else if (formStateCopy[key] === "" && (!fieldValidation.allow || !fieldValidation.allow.includes(""))) {
        formStateCopy[key] = null;
      }
    });
    return formStateCopy;
  };

  validateFile(file, element) {
    const errors = [];

    if (element.required && !file) {
      errors.push("No file selected.");
      return errors;
    }

    if (!file) {
      return errors;
    }

    const fileSizeKB = file.size / 1024;
    if (fileSizeKB > element.max_filesize) {
      errors.push(`Maximum file size is ${element.max_filesize} KB.`);
    }

    if (!element.mimetypes.includes(file.type)) {
      errors.push(`Only ${element.mimetypes.join(", ")} filetypes are allowed.`);
    }

    return errors;
  }

  handleFileChange(element, file) {
    let errors = [];
    if (element.required || file) {
      errors = this.validateFile(file, element);
    }

    if (errors.length > 0) {
      this.setState((prevState) => ({
        ...prevState,
        fieldErrors: {
          ...prevState.fieldErrors,
          [element.ref]: errors.join(", "),
        },
      }));
    } else {
      this.props.setFormState({
        ...this.props.formState,
        [element.ref]: file,
      });
      this.setState((prevState) => ({
        ...prevState,
        fieldErrors: {
          ...prevState.fieldErrors,
          [element.ref]: null,
        },
      }));
    }
  }

  validateField = (fieldName: string, fieldValue: any, isPhoneNumber?: boolean, index?: number) => {
    const uniqueFieldName = index !== undefined ? `${fieldName}_${index}` : fieldName;

    this.setState(
      (prevState) => ({
        formTouchedState: {
          ...prevState.formTouchedState,
          [uniqueFieldName]: true,
        },
      }),
      () => {
        const formStateCopy = this.removeEmptyStrings(this.props.formState);

        if (isPhoneNumber) {
          if (Array.isArray(fieldValue)) {
            fieldValue.forEach((value) => {
              this.validatePhoneNumberField(uniqueFieldName, value);
            });
          } else {
            this.validatePhoneNumberField(uniqueFieldName, fieldValue);
          }
        } else {
          this.validateSchemaField(uniqueFieldName, fieldName, formStateCopy);
        }
      },
    );
  };

  validatePhoneNumberField = (uniqueFieldName: string, phoneNumber: string) => {
    const isValid = phoneNumber ? this.validatePhoneNumber(phoneNumber) : true;

    this.setState((prevState) => ({
      fieldErrors: {
        ...prevState.fieldErrors,
        [uniqueFieldName]: !isValid ? "Invalid phone number" : null,
      },
    }));
  };

  validateSchemaField = (uniqueFieldName: string, fieldName: string, formStateCopy: any) => {
    const {error} = this.state.validationSchema.validate(formStateCopy, {abortEarly: false});

    if (this.state.formTouchedState[uniqueFieldName]) {
      const fieldError = error?.details.find((detail) => detail.path.includes(fieldName));
      this.setState((prevState) => ({
        fieldErrors: {
          ...prevState.fieldErrors,
          [uniqueFieldName]: fieldError ? fieldError.message : null,
        },
      }));
    }
  };

  isFormValid() {
    const formStateCopy = this.removeEmptyStrings(this.props.formState);
    const {
      fileFields,
      fileFieldErrors,
      fileErrors,
      formStateForValidation: formStateAfterFileProcessing,
    } = this.processFileFields({
      ...formStateCopy,
    });
    const {phoneFields, phoneFieldErrors, phoneErrors, formStateForValidation} = this.processPhoneNumberFields({
      ...formStateAfterFileProcessing,
    });

    const {error, value} = this.state.validationSchema.validate({...formStateForValidation}, {abortEarly: false});

    if (error || fileErrors || phoneErrors) {
      this.handleValidationErrors(error, fileFieldErrors, phoneFieldErrors);
    } else {
      const validatedForm = {...value, ...fileFields, ...phoneFields};

      this.scanKB();
      this.props.setValidatedForm(validatedForm);
    }
  }

  processFileFields(formStateForValidation) {
    const fileFields = {};
    let fileFieldErrors = {};
    let fileErrors = false;

    this.props.formContent.layout.elements.forEach((element) => {
      if (element.ui_type === "file") {
        const fileFieldValue = formStateForValidation[element.ref];
        const errors = this.validateFile(fileFieldValue, element);

        if (errors.length > 0) {
          fileFieldErrors[element.ref] = errors.join(", ");
          fileErrors = true;
        } else {
          fileFields[element.ref] = fileFieldValue;
          delete formStateForValidation[element.ref];
        }
      }
    });

    return {fileFields, fileFieldErrors, fileErrors, formStateForValidation};
  }

  processPhoneNumberFields(formStateForValidation) {
    const phoneFields = {};
    let phoneFieldErrors = {};
    let phoneErrors = false;

    this.props.formContent.layout.elements.forEach((element) => {
      const validation = this.props.formContent.validation.properties[element.ref];
      const isPhoneInput = element.ui_type === "input" && validation?.meta?.phone;
      const isPhoneList = element.ui_type === "list" && validation?.items?.meta?.phone;

      if (isPhoneInput) {
        phoneErrors = this.processPhoneInput(
          formStateForValidation,
          element,
          phoneFields,
          phoneFieldErrors,
          phoneErrors,
        );
      }

      if (isPhoneList) {
        phoneErrors = this.processPhoneList(
          formStateForValidation,
          element,
          phoneFields,
          phoneFieldErrors,
          phoneErrors,
        );
      }
    });

    return {phoneFields, phoneFieldErrors, phoneErrors, formStateForValidation};
  }

  processPhoneInput(formState, element, phoneFields, phoneFieldErrors, phoneErrors) {
    const phoneFieldValue = formState[element.ref];
    const isValid = phoneFieldValue ? this.validatePhoneNumber(phoneFieldValue) : true;

    if (!isValid) {
      phoneFieldErrors[element.ref] = "Invalid phone number";
      phoneErrors = true;
    } else {
      phoneFields[element.ref] = phoneFieldValue;
    }
    return phoneErrors;
  }

  processPhoneList(formState, element, phoneFields, phoneFieldErrors, phoneErrors) {
    const phoneFieldValues = formState[element.ref] || [];
    phoneFieldValues.forEach((phoneFieldValue, index) => {
      const isValid = phoneFieldValue ? this.validatePhoneNumber(phoneFieldValue) : true;

      if (!isValid) {
        phoneFieldErrors[`${element.ref}_${index}`] = "Invalid phone number";
        phoneErrors = true;
      } else {
        phoneFields[`${element.ref}_${index}`] = phoneFieldValue;
      }
    });
    return phoneErrors;
  }

  handleValidationErrors(joiError, fileFieldErrors, phoneFieldErrors) {
    const fieldErrors = {...fileFieldErrors, ...phoneFieldErrors};

    joiError?.details?.forEach((detail) => {
      const fieldName = detail.path[0];
      const fieldIndex = detail.path.length > 1 ? detail.path[1] : undefined;
      const uniqueFieldName = fieldIndex !== undefined ? `${fieldName}_${fieldIndex}` : fieldName;

      if (detail.type === "string.empty") {
        fieldErrors[uniqueFieldName] = detail.message;
      } else if (detail.type === "array.min" && detail.context.limit === 1) {
        fieldErrors[`${fieldName}_0`] = detail.message;
      } else {
        fieldErrors[uniqueFieldName] = detail.message;
      }
    });

    this.setState({fieldErrors});
  }

  validatePhoneNumber(phoneNumber: string) {
    if (typeof phoneNumber !== "string") {
      return false;
    }
    const phoneNumberObj = parsePhoneNumberFromString(phoneNumber);

    return phoneNumberObj?.isValid() ?? false;
  }
}

const mapStateToProps = (state: {questions: Question[]; settings: {settings: KBSettings}}) => {
  return {
    questions: state.questions,
    settings: state.settings.settings,
  };
};

export default connect(mapStateToProps)(withTranslation()(Form));
